/* ============================================================================
   รูปแบบวันที่ของทั้งระบบ — ที่เดียวที่แปลงวันที่เป็นข้อความให้คนอ่าน

   กติกา (ตกลงกันแล้ว ห้ามแตกแถว):
     วันที่          → YYYY-MM-DD           เช่น 2026-08-01
     วันที่ + เวลา   → YYYY-MM-DD HH:mm     เช่น 2026-08-01 09:30   (ไม่มีวินาที)
     ปี             → **ค.ศ. เท่านั้น**

   ── ทำไมไม่ใช้ toLocaleDateString / Intl ─────────────────────────────────────
   ⚠️ `toLocaleString('th-TH', …)` บนเครื่องที่ตั้ง locale ไทย **แปลงเป็น พ.ศ. ให้เอง**
      (2026 → 2569) โดยที่โค้ดไม่ได้สั่งอะไรเลย และเครื่องคนละเครื่องได้ผลไม่เหมือนกัน
      → ที่นี่จึงตัดจากสตริงล้วน ไม่เรียก API ที่ขึ้นกับ locale เลย

   ── ทำไมไม่ใช้ new Date(...).getMonth() ─────────────────────────────────────
   ⚠️ ค่าที่ backend ส่งมาเป็น **เวลาไทยที่ไม่ติด Z** (`2026-08-01T09:30:00`)
      พอเข้า new Date() เบราว์เซอร์ตีความตาม timezone ของเครื่อง แล้ว getHours()
      คืนค่าที่เลื่อนไปตามเครื่องผู้ใช้ — เครื่องที่ตั้งโซนผิดจะเห็นเวลาผิดแบบเงียบ ๆ
      → อ่านจากตำแหน่งตัวอักษรแทน ผลเหมือนกันทุกเครื่อง

   ⚠️ ไฟล์นี้มีคู่แฝดที่ frontend-liff/src/lib/datetime.ts — เนื้อในต้องเหมือนกัน
      (สอง frontend เป็นคนละ npm package ไม่มี workspace ให้ import ข้ามกันได้
       ถ้าแก้ที่นี่ต้องไปแก้อีกที่ด้วย ไม่งั้นสองหน้าจอแสดงวันที่ไม่ตรงกัน)
   ============================================================================ */

/** ค่าที่แสดงเมื่อไม่มีวันที่ — ใช้ตัวเดียวกันทุกที่ ไม่ใช่บางที่ '-' บางที่ว่าง */
export const NO_DATE = '-'

/** `2026-08-01…` (อะไรก็ได้ที่ขึ้นต้นด้วย YYYY-MM-DD) */
const DATE_HEAD = /^(\d{4})-(\d{2})-(\d{2})/
/** เวลา `…T09:30…` หรือ `… 09:30…` (รับทั้ง 2 แบบ เพราะ backend ใช้ CONVERT ทั้ง style 120 และ 126) */
const TIME_PART = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/

/**
 * ปีที่ยอมรับ — ใช้จับ พ.ศ. ที่หลุดมา (2569 > 2100) และค่าเริ่มต้นของ datetime (1900)
 * ⚠️ HR_LEAVE.LEAVE_DATE_E มีปี 1900 อยู่จริงจากระบบเดิม (ใช้แทน NULL) จึงต้องกันด้วย
 */
export const MIN_YEAR = 1970
export const MAX_YEAR = 2100

/** ปีนี้ดูเหมือน พ.ศ. ไหม — ใช้ทำข้อความ error ที่บอกสาเหตุได้ ไม่ใช่แค่ "รูปแบบผิด" */
export function looksBuddhist(year: number): boolean {
  return year > 2400 && year < 2700
}

/** `2026-08-01T09:30:00` / `2026-08-01 09:30:00` / `2026-08-01` → `2026-08-01` */
export function formatDate(raw: string | null | undefined): string {
  if (!raw) return NO_DATE
  const m = DATE_HEAD.exec(String(raw))
  // อ่านไม่ออกก็คืนค่าดิบ ดีกว่าโชว์ '-' แล้วเดาไม่ได้ว่าข้อมูลหน้าตายังไง
  if (!m) return String(raw)
  return `${m[1]}-${m[2]}-${m[3]}`
}

/**
 * → `2026-08-01 09:30` · ถ้าค่าไม่มีส่วนเวลา คืนแค่วันที่
 * วินาทีถูกตัดตามกติกา (ความละเอียดระดับวินาทีไม่มีใครใช้ตัดสินใจ แต่ทำให้คอลัมน์กว้างขึ้น)
 */
export function formatDateTime(raw: string | null | undefined): string {
  if (!raw) return NO_DATE
  const s = String(raw)
  const d = DATE_HEAD.exec(s)
  if (!d) return s
  const t = TIME_PART.exec(s)
  const date = `${d[1]}-${d[2]}-${d[3]}`
  return t ? `${date} ${t[1]}:${t[2]}` : date
}

/** ช่วงวันที่ — `2026-08-01 – 2026-08-31` / `ตั้งแต่ …` / `ถึง …` / `ไม่จำกัด` */
export function formatDateRange(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  if (!from && !to) return 'ไม่จำกัด'
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`
  if (from) return `ตั้งแต่ ${formatDate(from)}`
  return `ถึง ${formatDate(to)}`
}

/**
 * เลื่อนเวลาบนสตริงเป็นชั่วโมง — ใช้แปลง UTC (ร่องรอยระบบ) → เวลาไทยตอนแสดง
 *
 * ⚠️ คิดผ่าน Date.UTC แล้วอ่านกลับด้วย getUTC* เท่านั้น — **ไม่แตะ timezone ของเครื่อง**
 *    (ใช้ new Date('2026-08-01T09:30:00') เฉย ๆ จะได้ผลต่างกันในแต่ละเครื่อง)
 *    ต้องคิดด้วย epoch จริงเพราะการบวกชั่วโมงข้ามวัน/เดือน/ปีได้
 */
export function shiftHours(raw: string | null | undefined, hours: number): string | null {
  if (!raw) return null
  const s = String(raw)
  const d = DATE_HEAD.exec(s)
  if (!d) return null
  const t = TIME_PART.exec(s)

  const at = Date.UTC(
    Number(d[1]),
    Number(d[2]) - 1,
    Number(d[3]),
    t ? Number(t[1]) : 0,
    t ? Number(t[2]) : 0,
  )
  const out = new Date(at + hours * 3_600_000)
  const p2 = (n: number) => String(n).padStart(2, '0')
  return (
    `${out.getUTCFullYear()}-${p2(out.getUTCMonth() + 1)}-${p2(out.getUTCDate())}` +
    `T${p2(out.getUTCHours())}:${p2(out.getUTCMinutes())}`
  )
}

/** เวลาไทยเร็วกว่า UTC 7 ชั่วโมง (ไทยไม่มี DST — ค่าคงที่ใช้ได้ตลอด) */
const TH_OFFSET_HOURS = 7

/**
 * ค่าที่เก็บเป็น **UTC** (created_at / updated_at / imported_at ฯลฯ) → เวลาไทยที่อ่านรู้เรื่อง
 *
 * ⚠️ ใช้กับคอลัมน์ที่เป็น UTC เท่านั้น · คอลัมน์ที่เก็บเป็นเวลาไทยอยู่แล้ว
 *    (starts_at / ends_at / effective_* / TA_Time) ให้ใช้ formatDateTime ตรง ๆ
 *    ถ้าเอามาใช้ผิดตัวจะเลื่อนไป 7 ชั่วโมงแบบดูเหมือนถูก
 */
export function formatUtcAsThai(raw: string | null | undefined): string {
  if (!raw) return NO_DATE
  const shifted = shiftHours(raw, TH_OFFSET_HOURS)
  return shifted ? formatDateTime(shifted) : String(raw)
}

/* ─────────────────── ตรวจค่าที่ผู้ใช้กรอก (กัน พ.ศ.) ─────────────────── */

export type DateCheck = { ok: true; value: string } | { ok: false; error: string }

/** วันที่นี้มีอยู่จริงไหม — คิดด้วย Date.UTC ที่ไม่ขึ้นกับ timezone ของเครื่อง */
function isRealDate(y: number, mo: number, d: number): boolean {
  const t = new Date(Date.UTC(y, mo - 1, d))
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d
}

/**
 * ตรวจ `YYYY-MM-DD` ที่รับมาจากผู้ใช้ — คืนข้อความบอกสาเหตุที่คนแก้เองได้
 *
 * เหตุผลที่แยกเป็นฟังก์ชัน: ปฏิทินของเราส่งค่าถูกอยู่แล้ว แต่ค่ายังเข้ามาทาง
 * URL / ค่าที่ paste / ของเก่าที่ค้างใน state ได้ → ต้องมีด่านก่อนส่งขึ้น backend
 */
export function checkDate(raw: string | null | undefined): DateCheck {
  const s = String(raw ?? '').trim()
  if (s === '') return { ok: false, error: 'ยังไม่ได้เลือกวันที่' }

  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return { ok: false, error: `วันที่ต้องเป็นรูปแบบ YYYY-MM-DD (ได้ "${s}")` }

  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  if (looksBuddhist(y)) {
    return {
      ok: false,
      error: `ปี ${y} ดูเหมือนเป็น พ.ศ. — ระบบใช้ ค.ศ. เท่านั้น (พ.ศ. ${y} = ค.ศ. ${y - 543})`,
    }
  }
  if (y < MIN_YEAR || y > MAX_YEAR) {
    return { ok: false, error: `ปี ${y} อยู่นอกช่วงที่รับได้ (${MIN_YEAR}–${MAX_YEAR})` }
  }
  if (!isRealDate(y, mo, d)) return { ok: false, error: `ไม่มีวันที่ ${s} จริง` }

  return { ok: true, value: s }
}

/** ตรวจ `YYYY-MM-DD HH:mm` (รับ `T` คั่นด้วย) — คืนรูปแบบมาตรฐานที่ใช้ส่งขึ้น backend */
export function checkDateTime(raw: string | null | undefined): DateCheck {
  const s = String(raw ?? '').trim()
  if (s === '') return { ok: false, error: 'ยังไม่ได้เลือกวันที่และเวลา' }

  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})$/.exec(s)
  if (!m) return { ok: false, error: `ต้องเป็นรูปแบบ YYYY-MM-DD HH:mm (ได้ "${s}")` }

  const day = checkDate(m[1])
  if (!day.ok) return day

  const [hh, mi] = [Number(m[2]), Number(m[3])]
  if (hh > 23 || mi > 59) return { ok: false, error: `เวลา ${m[2]}:${m[3]} ไม่ถูกต้อง` }

  return { ok: true, value: `${day.value}T${m[2]}:${m[3]}` }
}

/* ─────────────────── "วันนี้" ตามเวลาไทย ─────────────────── */

/**
 * วันนี้/เวลานี้ตามเวลาไทย ในรูป `YYYY-MM-DD` และ `YYYY-MM-DDTHH:mm`
 *
 * ⚠️ อ่านผ่าน Intl ที่ระบุ timeZone: 'Asia/Bangkok' ไม่ใช่ getDate() ของเครื่อง
 *    เพราะเครื่องที่ตั้ง timezone ผิด (หรือ VM/remote desktop) จะได้วันผิดเงียบ ๆ
 *    ตรงนี้ใช้ Intl ได้เพราะระบุ locale เป็น en-CA ซึ่งให้ ISO และไม่มีเรื่อง พ.ศ.
 */
function thaiParts(hoursAhead = 0): { date: string; time: string } {
  const at = new Date(Date.now() + hoursAhead * 3_600_000)
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at)
  const get = (type: string) => f.find((p) => p.type === type)?.value ?? ''
  // ICU บางรุ่นคืนเที่ยงคืนเป็น '24' — <input> และการเทียบสตริงรับไม่ได้
  const hour = get('hour') === '24' ? '00' : get('hour')
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${hour}:${get('minute')}`,
  }
}

/** วันนี้ตามเวลาไทย `YYYY-MM-DD` */
export function todayThai(): string {
  return thaiParts().date
}

/** เวลาไทยตอนนี้ (บวกชั่วโมงได้) `YYYY-MM-DDTHH:mm` */
export function nowThai(hoursAhead = 0): string {
  const p = thaiParts(hoursAhead)
  return `${p.date}T${p.time}`
}

/**
 * วันแรกถึงวันสุดท้ายของเดือนที่วันนั้นอยู่ — ค่าเริ่มต้นของตัวกรองช่วงวันที่
 *
 * @param date วันอ้างอิง `YYYY-MM-DD` (ไม่ส่ง = วันนี้ตามเวลาไทย)
 *
 * ⚠️ หาวันสุดท้ายด้วย `Date.UTC(y, m, 0)` — เดือนใน JS เริ่มที่ 0 ฉะนั้น `m` ที่เป็น
 *    เลขเดือนแบบ 1–12 จึงหมายถึง "เดือนถัดไป" และ day = 0 คือ "ก่อนวันที่ 1 ของเดือนถัดไป"
 *    = วันสุดท้ายของเดือนที่ต้องการ · คิดปีอธิกสุรทินให้เองถูกต้อง (ก.พ. 2028 = 29)
 *
 * ⚠️ ใช้ `Date.UTC` ไม่ใช่ `new Date(y, m, 0)` — ตัวหลังคิดตาม timezone ของเครื่อง
 *    เครื่องที่ตั้งโซนต่างกันจะได้วันสุดท้ายไม่เท่ากันตรงขอบเดือน (กติกาหัวไฟล์)
 */
export function monthRangeThai(date: string = todayThai()): { from: string; to: string } {
  const year = Number(date.slice(0, 4))
  const month = Number(date.slice(5, 7))
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const mm = String(month).padStart(2, '0')

  return {
    from: `${year}-${mm}-01`,
    to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
  }
}
